import { captureRemixErrorBoundaryError, withSentry } from "@sentry/remix";
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useRouteError,
} from "@remix-run/react";
import { getSessionUser } from "~/utils/firebase.server";
import { prisma } from "~/utils/db.server";
import { normalizeEmail } from "~/utils/email.server";
import type { LinksFunction } from "@remix-run/node";
import { useEffect, useState } from "react";

import "./tailwind.css";
import Loader from "./components/Loader";
import { NavBar } from "./components/NavBar";
import { PwaInstallPrompt } from "./components/PwaInstallPrompt";

export const links: LinksFunction = () => [
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  {
    rel: "preconnect",
    href: "https://fonts.gstatic.com",
    crossOrigin: "anonymous",
  },
  {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:ital,wght@0,200..800;1,200..800&display=swap",
  },
  { rel: "manifest", href: "/manifest.json" },
  { rel: "apple-touch-icon", href: "/icon-192.png" },
  { rel: "icon", type: "image/png", sizes: "192x192", href: "/icon-192.png" },
  { rel: "icon", type: "image/png", sizes: "512x512", href: "/icon-512.png" },
];

const ADMIN_EMAIL = "sep@hermansgroep.nl";

export async function loader({ request }: { request: Request }) {
  const sessionUser = await getSessionUser(request);

  const email = normalizeEmail(sessionUser?.email);
  let user = null;
  if (email) {
    // Ensure sep@hermansgroep.nl is always admin (case-insensitive)
    if (email === normalizeEmail(ADMIN_EMAIL)) {
      user = await prisma.user.upsert({
        where: { email },
        update: { isAdmin: true },
        create: {
          email,
          name: email.split("@")[0],
          isAdmin: true,
        },
      });
    } else {
      user = await prisma.user.findUnique({
        where: { email },
      });
    }
  }

  return { user };
}

export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
      </head>
      <body>
        <div className="min-h-screen w-full bg-bar-dark flex flex-col">
          
          {children}
        </div>
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

function App() {
  const { user } = useLoaderData<typeof loader>();
  console.log(`user`, user);  
  const [showSplash, setShowSplash] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setShowSplash(false), 500);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
        {showSplash ? (
          <div className="flex items-center justify-center h-screen animate-fade-in">
            <Loader size={8} />
          </div>
        ) : (
          <>
          <NavBar user={user} />
          <Outlet context={{ user }} />
          <PwaInstallPrompt />
          </>
        )}
    </>
  );
}

export const ErrorBoundary = () => {
  const error = useRouteError();
  captureRemixErrorBoundaryError(error);
  return (
    <div className="min-h-screen flex items-center justify-center bg-bar-dark">
      <div className="text-center p-8">
        <h1 className="text-2xl font-bold text-bar-cream mb-2">Something went wrong</h1>
        <p className="text-bar-muted">We&apos;re sorry, but something unexpected happened.</p>
      </div>
    </div>
  );
};

export default withSentry(App);